// src/utils/validators/ugc/mediaValidator.ts
import { getVideoDurationSeconds } from "./videoMetadata";

export type MediaKind = "image" | "video";

export type ValidationOk<TMeta = undefined> = { ok: true; meta?: TMeta };
export type ValidationFail = { ok: false; error: string };

export type ImagePolicy = {
  maxMB: number;
  mimePrefix?: string; // default: "image/"
};

export type VideoPolicy = {
  minDurationSec: number;
  maxDurationSec: number;

  // size policy:
  // maxSizeMB = min(capMB, mbPerMinute * duration_minutes)
  capMB: number;
  mbPerMinute: number;

  mimePrefix?: string; // default: "video/"
};

export type VideoTier = {
  maxDurationSec: number;
  capMB: number;
  mbPerMinute: number;
};

export type TieredVideoPolicy = VideoPolicy & {
  tiers: VideoTier[];
};

export type AnyVideoPolicy = VideoPolicy | TieredVideoPolicy;

export const formatMB = (bytes: number) =>
  Math.round((bytes / 1024 / 1024) * 10) / 10;

function hasTiers(p: AnyVideoPolicy): p is TieredVideoPolicy {
  return Array.isArray((p as TieredVideoPolicy).tiers);
}

function resolveTierForDuration(
  durationSec: number,
  p: TieredVideoPolicy
): VideoTier {
  // pick first tier that can hold this duration; otherwise fallback to last tier
  const sorted = [...p.tiers].sort((a, b) => a.maxDurationSec - b.maxDurationSec);
  return (
    sorted.find((t) => durationSec <= t.maxDurationSec) ?? sorted[sorted.length - 1]
  );
}

export function computeMaxVideoMB(durationSec: number, p: AnyVideoPolicy): number {
  const minutes = durationSec / 60;

  // tiered policy: swap cap/mbPerMinute based on duration
  if (hasTiers(p)) {
    const tier = resolveTierForDuration(durationSec, p);
    const byDuration = tier.mbPerMinute * minutes;
    const max = Math.min(tier.capMB, byDuration);
    return Math.round(max * 10) / 10; // 0.1MB
  }

  // simple policy
  const byDuration = p.mbPerMinute * minutes;
  const max = Math.min(p.capMB, byDuration);
  return Math.round(max * 10) / 10; // 0.1MB
}

export function validateImageFile(
  file: File,
  p: ImagePolicy
): ValidationOk | ValidationFail {
  const mimePrefix = p.mimePrefix ?? "image/";
  if (!file.type?.startsWith(mimePrefix)) {
    return { ok: false, error: "Please select a valid image file." };
  }

  const sizeMB = formatMB(file.size);
  if (sizeMB > p.maxMB) {
    return {
      ok: false,
      error: `Image is too large (${sizeMB}MB). Max allowed is ${p.maxMB}MB.`,
    };
  }

  return { ok: true };
}

export async function validateVideoFile(
  file: File,
  p: AnyVideoPolicy
): Promise<
  ValidationOk<{ durationSec: number; maxAllowedMB: number }> | ValidationFail
> {
  const mimePrefix = p.mimePrefix ?? "video/";
  if (!file.type?.startsWith(mimePrefix)) {
    return { ok: false, error: "Please select a valid video file." };
  }

  let durationSec: number;
  try {
    durationSec = await getVideoDurationSeconds(file);
  } catch {
    return {
      ok: false,
      error: "Unable to read video length. Please try another file.",
    };
  }

  if (durationSec < p.minDurationSec) {
    return {
      ok: false,
      error: `Video is too short (${Math.floor(
        durationSec
      )}s). Minimum is ${p.minDurationSec}s.`,
    };
  }

  if (durationSec > p.maxDurationSec) {
    return {
      ok: false,
      error: `Video is too long (${Math.ceil(
        durationSec
      )}s). Maximum is ${p.maxDurationSec}s.`,
    };
  }

  const maxAllowedMB = computeMaxVideoMB(durationSec, p);
  const sizeMB = formatMB(file.size);

  if (sizeMB > maxAllowedMB) {
    return {
      ok: false,
      error: `Video is too large (${sizeMB}MB). For a ${Math.round(
        durationSec
      )}s video, max allowed is ${maxAllowedMB}MB.`,
    };
  }

  return { ok: true, meta: { durationSec, maxAllowedMB } };
}
